import { useState } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { CalendarDays } from "lucide-react";
import { Modal }          from "@/components/ui/Modal";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency, formatNumber } from "@/utils/format";
import { MesStat } from "../chartUtils";
import { ModalEntityTitle, OutcomeTable, IncomeTable, StatementTable } from "./FinanceiroModalContent";
import { BAR_HOVER_PROPS, lineDots, TOOLTIP_CURSOR_BAND, barRowFromClick } from "@/utils/rechartsInteraction";

/* ─── Tooltip ─────────────────────────────────────────────────────────────── */
interface TooltipProps {
  active?:  boolean;
  payload?: { value: number; name: string; color: string }[];
  label?:   string;
}
function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-modal p-3 min-w-[200px]">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{label}</p>
      {payload.map((item) => (
        <div key={item.name} className="flex items-center justify-between gap-4 text-sm py-0.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
            <span className="text-slate-600">{item.name}</span>
          </span>
          <span className="font-semibold text-slate-800">
            {item.name === "Saldo (R$)"
              ? formatCurrency(item.value)
              : formatCurrency(item.value)}
          </span>
        </div>
      ))}
      <p className="text-[10px] text-slate-400 mt-2">Clique para ver detalhes</p>
    </div>
  );
}

/* ─── Tabs do modal ──────────────────────────────────────────────────────── */
type Tab = "despesas" | "receitas" | "extrato";

/* ─── Componente ─────────────────────────────────────────────────────────── */
interface Props {
  data:    MesStat[];
  loading: boolean;
}

export function FluxoMensal({ data, loading }: Props) {
  const [selected, setSelected] = useState<MesStat | null>(null);
  const [tab,      setTab]      = useState<Tab>("despesas");

  function handleClick(item: unknown) {
    const row = barRowFromClick<MesStat>(item);
    if (row) {
      setSelected(row);
      setTab("despesas");
    }
  }

  return (
    <>
      <div className="flex h-full min-h-0 flex-col">
        <ChartContainer
          title="Fluxo de Caixa Mensal"
          subtitle="Parcelas pagas / títulos recebidos + extrato SEMOG (por mês)"
          loading={loading}
          empty={!loading && data.length === 0}
          height={165}
          fillSpace
          className="flex min-h-0 flex-1 flex-col gap-3 p-4"
        >
        <ComposedChart
          data={data}
          margin={{ top: 4, right: 52, left: 4, bottom: 0 }}
          barGap={1}
          barCategoryGap="8%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis
            dataKey="mes"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            tickMargin={6}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            width={56}
            tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 11, fill: "#94a3b8" }}
            axisLine={false}
            tickLine={false}
            width={56}
            tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} cursor={TOOLTIP_CURSOR_BAND} />
          <Legend
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{ fontSize: 10, paddingTop: 0, paddingBottom: 0 }}
            iconType="circle"
            iconSize={7}
            height={22}
          />
          <Bar
            {...BAR_HOVER_PROPS}
            yAxisId="left"
            dataKey="receitas"
            name="Receitas (R$)"
            fill="#10b981"
            radius={[4, 4, 0, 0]}
            maxBarSize={34}
            cursor="pointer"
            onClick={handleClick}
          />
          <Bar
            {...BAR_HOVER_PROPS}
            yAxisId="left"
            dataKey="despesas"
            name="Despesas (R$)"
            fill="#B03858"
            radius={[4, 4, 0, 0]}
            maxBarSize={34}
            cursor="pointer"
            onClick={handleClick}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="saldo"
            name="Saldo (R$)"
            stroke="#3b82f6"
            strokeWidth={2}
            {...lineDots("#3b82f6")}
          />
        </ComposedChart>
        </ChartContainer>
      </div>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        size="xl"
        title={selected && (
          <ModalEntityTitle
            icon={<CalendarDays className="w-4 h-4" />}
            label={selected.mes}
            iconBg="bg-blue-50"
            iconColor="text-blue-600"
          />
        )}
      >
        {selected && (
          <div className="flex flex-col gap-4">
            {/* KPI mini-cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-emerald-50 rounded-xl p-3 text-center">
                <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">Receitas</p>
                <p className="text-lg font-bold text-slate-800 mt-0.5">{formatCurrency(selected.receitas, true)}</p>
              </div>
              <div className="bg-rose-50 rounded-xl p-3 text-center">
                <p className="text-xs font-semibold text-rose-600 uppercase tracking-wide">Despesas</p>
                <p className="text-lg font-bold text-slate-800 mt-0.5">{formatCurrency(selected.despesas, true)}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 text-center">
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Saldo</p>
                <p className={`text-lg font-bold mt-0.5 ${selected.saldo >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {formatCurrency(selected.saldo, true)}
                </p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-100">
              {(["despesas", "receitas", "extrato"] as Tab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    tab === t
                      ? "border-brand-600 text-brand-600"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {t === "despesas"
                    ? `Despesas (${formatNumber(selected.outcomeItems.length)})`
                    : t === "receitas"
                      ? `Receitas (${formatNumber(selected.incomeItems.length)})`
                      : `Extrato (${formatNumber(selected.statementItems.length)})`}
                </button>
              ))}
            </div>

            {tab === "despesas" && <OutcomeTable items={selected.outcomeItems} />}
            {tab === "receitas" && <IncomeTable  items={selected.incomeItems}  />}
            {tab === "extrato" && <StatementTable items={selected.statementItems} />}
          </div>
        )}
      </Modal>
    </>
  );
}
